/* eslint-disable react/no-multi-comp */
import moment from 'moment';
import numeral from 'numeral';
import React from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash.noop';
import { useQuery } from 'react-apollo';
import styles from './style.css';

import { top10Holdings, getHoldingsTitle } from './util';
import { endOfMonth } from '../FundList/util';
import closeButton from '../../images/icon_cross_black.svg';
import AllocationChartAndLegend from '../AllocationChartAndLegend/AllocationChartAndLegend';
import { mapFundAllocation } from '../../hooks/useCartDetailsState';
import Loader from '../Loader/Loader';
import { TOP_10_HOLDINGS_QUERY } from './queries';
import { MFBeaconButton, MFBeaconIconButton } from '../Beacon/BeaconComponents';
import { isEmptyArray, isEmptyObject } from '../../core/util';

// TODO [2020-05-25]: pull over format utils from MFRT
const priceOrNA = price =>
  parseFloat(price) ? `$${numeral(price).format('0,0[.]00')}` : 'N/A';

export const formatDate = date =>
  date ? moment.parseZone(date).format('MM/DD/YYYY') : 'N/A';

const setProspectusLink = fund => {
  if (!fund?.cusip) {
    return 'https://www.troweprice.com/personal-investing/funds/mutual-funds/prospectuses-reports.html';
  }

  return `https://prospectus-express.broadridge.com/summary.asp?doctype=pros&clientid=trowepll&fundid=${fund?.cusip}`;
};

const InvestmentObjectiveSection = ({ fund }) => (
  <div className="section">
    <div className="section-title">Fund Prospectus</div>
    <div className="prospectus">
      <a
        href={setProspectusLink(fund)}
        target="_blank"
        rel="noopener noreferrer">
        Download a prospectus
      </a>
    </div>
    <style jsx>{styles}</style>
  </div>
);

const QuickStatsSection = ({ fund }) => (
  <div className="section">
    <div className="section-title">Quick Stats</div>
    <div>
      <ul>
        <li>
          <span>Current NAV as of {formatDate(fund?.price?.asOfDate)}</span>
          <span className="pull-right">{priceOrNA(fund?.price?.price)}</span>
        </li>
        <li>
          <span>NAV Change</span>
          <span className="pull-right">
            {priceOrNA(fund?.price?.priceChange)}
          </span>
        </li>
        <li>
          <span>NAV 12 Month Low-High </span>
          <span className="pull-right">
            {fund?.twelveMonthLowPrice && fund?.twelveMonthHighPrice && (
              <span>
                {priceOrNA(fund?.twelveMonthLowPrice?.price)} -{' '}
                {priceOrNA(fund?.twelveMonthHighPrice?.price)}
              </span>
            )}
          </span>
        </li>
        <li>
          <span>Net Assets as of {formatDate(fund?.netAssetsAsOfDate)}</span>
          <span className="pull-right">{priceOrNA(fund?.netAssets)}</span>
        </li>
      </ul>
    </div>
    <style jsx>{styles}</style>
  </div>
);

const PortfolioManagerSection = ({ fund }) => (
  <div className="section portfolio-manager">
    <div className="section-title">Portfolio Manager</div>

    {fund.mutualFundManagers.map(manager => {
      return (
        <div key={manager.index}>
          <div>{manager.managerName}</div>

          <div>
            {manager.managedFundSinceDate && (
              <p className="managed-fund-since-date">
                Managed Fund Since:{' '}
                {moment(manager.managedFundSinceDate).format('YYYY')}
              </p>
            )}

            {manager.joinedFirmDate && (
              <p className="joined-firm-date">
                Joined Firm: {moment(manager.joinedFirmDate).format('YYYY')}
              </p>
            )}
          </div>
        </div>
      );
    })}
    <style jsx>{styles}</style>
  </div>
);

const QVFundCompositionSection = ({ fund }) => {
  const { allocation } = mapFundAllocation(fund);
  const {
    stocks,
    bonds,
    shortTermReserves,
    fundAllocationsPresent
  } = allocation;
  return (
    <>
      {fundAllocationsPresent && (
        <div className="section">
          <div className="section-title">Fund Composition</div>
          <AllocationChartAndLegend
            stocks={stocks}
            bonds={bonds}
            shortTerm={shortTermReserves}
          />
          <style jsx>{styles}</style>
        </div>
      )}
    </>
  );
};

const TopTenHoldingsSection = ({ ticker }) => {
  const { loading, data = {} } = useQuery(TOP_10_HOLDINGS_QUERY, {
    variables: { ticker }
  });
  const { mutualFund: { selectiveDisclosures = {}, bond = false } = {} } = data;

  if (loading) return <Loader smaller />;
  if (isEmptyObject(selectiveDisclosures)) return <span />;

  const latestMonthsData = isEmptyArray(selectiveDisclosures?.monthly)
    ? []
    : selectiveDisclosures?.monthly[0];
  const holdings = isEmptyArray(latestMonthsData.individualHoldings)
    ? []
    : top10Holdings(latestMonthsData.individualHoldings);

  const holdingsTitle = getHoldingsTitle(bond, holdings);
  const holdingsLeft = holdings.slice(0, 5);
  const holdingsRight = holdings.slice(5);
  const holdingsDisclaimer =
    'The total of the holdings in the table may not equal the percentage of total net assets shown because of rounding. Holdings are shown at the issuer/company level as of the date indicated and are subject to change.';

  return (
    holdings?.length > 0 && (
      <div className="section top-10-holdings">
        <div className="section-title">{holdingsTitle}</div>
        <div>
          <div>
            Represents {latestMonthsData?.top10CalculatedPercentage?.toFixed(2)}
            % of Total Net Assets
          </div>
          <div className="qv-col col-left">
            <ul>
              {holdingsLeft.map(item => {
                return (
                  <li key={`holdings-left-${item?.name}-${item?.rank}`}>
                    {item?.name}
                    <span className="holdings-net-percent">
                      {item?.percentTotalNetAssets?.toFixed(2)}%
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="qv-col col-right">
            <ul>
              {holdingsRight.map(item => {
                return (
                  <li key={`holdings-right-${item?.name}-${item?.rank}`}>
                    {item?.name}
                    <span className="holdings-net-percent">
                      {item?.percentTotalNetAssets?.toFixed(2)}%
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="holdings-disclaimer">{holdingsDisclaimer}</div>
        </div>
        <style jsx>{styles}</style>
      </div>
    )
  );
};

export const AddButton = ({ fund, disabled, addFund }) => (
  <>
    <MFBeaconButton
      id="addFundBtn"
      data-name="addFundBtn"
      onClick={() =>
        !disabled &&
        addFund({
          name: fund.name,
          ticker: fund.ticker,
          amount: 0,
          percent: 0,
          unlockedFund: fund.unlockedFund
        })
      }
      disabled={disabled}
      type="button"
      variant="secondary"
      label="Add This Fund"
    />
    <style jsx>{styles}</style>
  </>
);

const AddedButton = ({ fund, removeFund }) => (
  <>
    <MFBeaconButton
      onClick={() => removeFund(fund.ticker)}
      type="button"
      variant="secondary"
      label="Fund Added"
    />
    <style jsx>{styles}</style>
  </>
);

const QuickView = ({
  fund,
  id,
  disabled,
  addFund,
  removeFund,
  fundAdded,
  onClose = noop
}) => (
  <div className="quick-view">
    <div className="qv-row">
      <div className="qv-col col-left">
        <span className="small-text">as of {endOfMonth()}</span>
      </div>
      <div className="qv-col col-right">
        <span className="fund-details-close-btn">
          <MFBeaconIconButton
            id="rdfFundDetailsCloseBtn"
            data-name="closeFundDetailsBtn"
            type="button"
            className="close-button"
            onClick={() => onClose()}
            variant="tertiary"
            label="close-button">
            <img
              alt="Close fund details."
              slot="icon"
              className="close-btn-img"
              id={`closeBtn_${id}`}
              src={closeButton}
            />
          </MFBeaconIconButton>
        </span>
      </div>
    </div>

    <div className="qv-col col-left">
      <div className="qv-row">
        <InvestmentObjectiveSection fund={fund} />
      </div>
      <div className="qv-row">
        <QuickStatsSection fund={fund} />
      </div>
    </div>

    <div className="qv-col col-right">
      {fund.mutualFundManagers?.length > 0 && (
        <div className="qv-row">
          <PortfolioManagerSection fund={fund} />
        </div>
      )}
      <div className="qv-row">
        <QVFundCompositionSection fund={fund} />
      </div>
    </div>

    <div className="qv-row">
      <TopTenHoldingsSection ticker={fund?.ticker} />
    </div>

    {fundAdded && (
      <div className="add-fund-btn">
        <div className="section">
          {fundAdded(fund?.ticker) ? (
            <AddedButton fund={fund} removeFund={removeFund} />
          ) : (
            <AddButton fund={fund} disabled={disabled} addFund={addFund} />
          )}
        </div>
      </div>
    )}

    <style jsx>{styles}</style>
  </div>
);

QuickView.propTypes = {
  onClose: PropTypes.func
};

export default QuickView;
